import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  TextField,
  Typography,
  Box
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colors } from '../../themes/typography';
import { responsiveTypography, responsiveSpacing } from '../../themes/responsive';

const RejectReasonDialog = ({ open, onClose, onConfirm, loading = false, experimentCount = 1 }) => {
  const { t } = useTranslation();
  const [selectedReason, setSelectedReason] = React.useState('');
  const [customReason, setCustomReason] = React.useState('');

  const predefinedReasons = [
    { id: 'no_fit', label: t('noFit') },
    { id: 'out_of_stock', label: t('outOfStock') },
    { id: 'wont_sell', label: t('wontSell') },
    { id: 'low_margin', label: t('lowMargin') },
    { id: 'custom', label: t('other') }
  ];

  const handleReasonChange = (event) => {
    setSelectedReason(event.target.value);
    if (event.target.value !== 'custom') {
      setCustomReason('');
    }
  };

  const handleConfirm = () => {
    const reason = selectedReason === 'custom' ? customReason : 
                  predefinedReasons.find(r => r.id === selectedReason)?.label || '';
    
    if (reason.trim()) {
      onConfirm(reason);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedReason('');
    setCustomReason('');
    onClose();
  };

  const isCustomSelected = selectedReason === 'custom';
  const canConfirm = selectedReason && (selectedReason !== 'custom' || customReason.trim());

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '12px',
          boxShadow: `0 8px 32px ${colors.shadow.medium}`
        }
      }}
    >
      <DialogTitle sx={{
        ...responsiveTypography.h3,
        color: colors.text.primary,
        fontWeight: 600,
        pb: responsiveSpacing.sm
      }}>
        {experimentCount > 1 
          ? t('rejectMultipleExperiments', { count: experimentCount, defaultValue: `Reject ${experimentCount} Experiments` })
          : t('rejectExperiment')}
      </DialogTitle>
      
      <DialogContent sx={{ py: responsiveSpacing.md }}>
        <Typography sx={{
          ...responsiveTypography.body,
          color: colors.text.secondary,
          mb: responsiveSpacing.lg
        }}>
          {experimentCount > 1 
            ? t('selectRejectReasonMultiple', { defaultValue: 'Please select a reason for rejecting these experiments' })
            : t('selectRejectReason')}
        </Typography>

        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            value={selectedReason}
            onChange={handleReasonChange}
            sx={{
              '& .MuiFormControlLabel-root': {
                mb: responsiveSpacing.sm,
                borderRadius: '8px',
                border: `1px solid ${colors.border.light}`,
                p: responsiveSpacing.sm,
                m: 0,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: colors.background.subtle,
                },
                '& .MuiRadio-root': {
                  color: colors.action.primary,
                  '&.Mui-checked': {
                    color: colors.action.primary,
                  }
                }
              }
            }}
          >
            {predefinedReasons.map((reason) => (
              <FormControlLabel
                key={reason.id}
                value={reason.id}
                control={<Radio />}
                label={
                  <Typography sx={{
                    ...responsiveTypography.body,
                    color: colors.text.primary,
                    fontWeight: 500
                  }}>
                    {reason.label}
                  </Typography>
                }
              />
            ))}
          </RadioGroup>
        </FormControl>

        {isCustomSelected && (
          <Box sx={{ mt: responsiveSpacing.md }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder={t('enterCustomReason')}
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: colors.background.surface,
                  '& fieldset': {
                    borderColor: colors.border.medium,
                  },
                  '&:hover fieldset': {
                    borderColor: colors.border.dark,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: colors.action.primary,
                  },
                }
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        p: responsiveSpacing.lg,
        gap: responsiveSpacing.sm
      }}>
        <Button 
          onClick={handleClose}
          disabled={loading}
          sx={{
            ...responsiveTypography.body,
            textTransform: 'none',
            fontWeight: 500,
            color: colors.text.secondary,
            '&:hover': {
              backgroundColor: colors.background.subtle,
            }
          }}
        >
          {t('cancel')}
        </Button>
        <Button 
          onClick={handleConfirm}
          disabled={!canConfirm || loading}
          variant="contained"
          sx={{
            ...responsiveTypography.body,
            textTransform: 'none',
            fontWeight: 600,
            backgroundColor: '#ef5350',
            color: 'white',
            borderRadius: '8px',
            px: responsiveSpacing.lg,
            '&:hover': {
              backgroundColor: '#d32f2f',
            },
            '&:disabled': {
              backgroundColor: colors.background.subtle,
              color: colors.text.secondary,
            }
          }}
        >
          {loading ? t('loading') : t('rejectExperiment')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RejectReasonDialog;